// src/components/sections/LinkedInRecommendations.tsx
//
// Grille de recommandations LinkedIn (preuves sociales tierces).
// - Cards autonomes avec avatar à initiales (gradient brand)
// - Layout masonry-ish responsive (3 cols desktop, 2 cols tablet, 1 col mobile)
// - Badge "LinkedIn" subtil pour signaler la source des recos
// - Pas de noms complets ni de photos (anonymisation : initiales + rôle)
// - Voix réécrite pour parler du CABINET et pas du fondateur

import { motion } from 'framer-motion';
import { Linkedin } from 'lucide-react';

interface Recommendation {
  /** Initiales (ex: "M.D.") — gardent l'identité confidentielle */
  initials: string;
  /** Rôle / titre LinkedIn (ex: "Responsable pôle commerce") */
  role: string;
  /** Année de la reco (signal d'ancienneté) */
  year: number;
  /** Relation au cabinet ("client" / "candidat") */
  relation: 'client' | 'candidat' | 'partenaire';
  /** Citation réécrite — focus cabinet, pas personnel */
  quote: string;
}

const RECOMMENDATIONS: Recommendation[] = [
  {
    initials: 'M.L.',
    role: 'Responsable de magasin',
    year: 2018,
    relation: 'client',
    quote:
      "Comment passer à côté de ce cabinet ? On ne peut pas. L'empathie, la disponibilité, l'intelligence du recrutement : des échanges qualitatifs, pas un défilé de profils. Ce n'est pas une simple agence de chasseurs de tête — c'est un profileur dans sa matière. Réactivité, disponibilité, conseil, efficacité.",
  },
  {
    initials: 'C.V.',
    role: 'Purchasing Manager',
    year: 2017,
    relation: 'partenaire',
    quote:
      "Un partenaire de choix : accessible, disponible, transparent. Ces qualités créent un climat de confiance et un vrai partenariat — pas une simple prestation. Performance durable, bon sens de la négociation, synergies gagnant-gagnant.",
  },
  {
    initials: 'O.N.',
    role: 'Responsable pôle commerce',
    year: 2018,
    relation: 'client',
    quote:
      "Professionnalisme et accessibilité. À l'écoute des candidats, le suivi tout au long du process a permis d'instaurer un rapport de confiance fort. Je ne peux que recommander recruteurs.io.",
  },
  {
    initials: 'A.M.',
    role: 'Digital Project Manager · PMO',
    year: 2018,
    relation: 'candidat',
    quote:
      "Agréablement surprise par l'esprit d'analyse du cabinet : ils ont su pointer mes atouts et les mettre en avant pour ma candidature. Une connaissance fine du secteur, des conseils précieux, une vraie écoute, beaucoup d'accessibilité.",
  },
  {
    initials: 'M.F.',
    role: 'Chargée de développement RH · Handicap & QVCT',
    year: 2019,
    relation: 'partenaire',
    quote:
      "Une bienveillance rare, un vrai sens de l'écoute, beaucoup de dynamisme. Un atout infaillible si vous cherchez une nouvelle opportunité — comme candidat·e ou comme employeur.",
  },
  {
    initials: 'D.I.',
    role: 'Formatrice Conseil en Image · Retail Mode',
    year: 2017,
    relation: 'partenaire',
    quote:
      "Une vraie compétence pour positionner le bon candidat au bon poste, et une connaissance fine du secteur. Un soutien technique et humain à la fois — que l'on soit candidat ou recruteur. Humble, professionnel, disponible, efficace.",
  },
  {
    initials: 'M.D.',
    role: 'Communication & Numérique · ARDML PACA',
    year: 2023,
    relation: 'partenaire',
    quote:
      "Une vraie qualité chez recruteurs.io : l'empathie comme socle, sans rien lâcher sur l'organisation, les relances humaines, ou l'approche innovante des processus RH. Le bon partenaire dans la majorité des situations.",
  },
];

const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const RELATION_LABEL: Record<Recommendation['relation'], string> = {
  client: 'Client',
  candidat: 'Candidat·e',
  partenaire: 'Partenaire',
};

interface Props {
  /** Background variant — défaut "light" (fbfaf8), pour alterner avec d'autres sections */
  background?: 'light' | 'white';
  /** Eyebrow personnalisable */
  eyebrow?: string;
}

export default function LinkedInRecommendations({
  background = 'light',
  eyebrow = 'Recommandations vérifiées',
}: Props) {
  const bgClass = background === 'light' ? 'bg-[#fbfaf8]' : 'bg-white';

  return (
    <section className={`${bgClass} py-16 md:py-20 lg:py-24`}>
      <div className="container-custom">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-14">
          <span className={eyebrowClass}>
            <Linkedin className="w-3.5 h-3.5 mr-2 text-[#0077b5]" />
            {eyebrow}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]">
            Ce que disent ceux qui{' '}
            <span className={gradientText}>nous ont essayés.</span>
          </h2>
          <p className="text-lg md:text-xl text-neutral-600 leading-relaxed">
            Sept recommandations laissées <strong>sur LinkedIn</strong>, certaines depuis 2017.
            Initiales et rôles préservés (anonymat consenti) — les profils complets sont
            consultables sur demande.
          </p>
        </div>

        {/* Grid de cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
          {RECOMMENDATIONS.map((reco, idx) => (
            <motion.div
              key={reco.initials + reco.year}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.45, delay: idx * 0.04 }}
              className="relative flex flex-col rounded-2xl bg-white ring-1 ring-neutral-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-6"
            >
              {/* Top : avatar + initiales + rôle + badge LinkedIn */}
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold text-sm shadow-md shadow-[#d03484]/20">
                  {reco.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="text-sm font-bold text-[#30475e]">
                      {reco.initials}
                    </span>
                    <span
                      className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-sm bg-[#0077b5]"
                      title="Recommandation LinkedIn"
                      aria-label="Recommandation LinkedIn"
                    >
                      <Linkedin className="w-2.5 h-2.5 text-white" />
                    </span>
                  </div>
                  <p className="text-xs text-neutral-500 leading-snug">
                    {reco.role}
                  </p>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="flex-1 text-sm text-neutral-700 leading-relaxed mb-4">
                « {reco.quote} »
              </blockquote>

              {/* Footer card : badge relation + année */}
              <div className="flex items-center justify-between pt-3 border-t border-neutral-100 text-[10px] uppercase tracking-widest font-semibold">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 ${
                    reco.relation === 'client'
                      ? 'bg-[#d03484]/10 text-[#d03484]'
                      : reco.relation === 'candidat'
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {RELATION_LABEL[reco.relation]}
                </span>
                <span className="text-neutral-400">{reco.year}</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer disclaimer + CTA discret */}
        <p className="mt-10 text-center text-sm text-neutral-500 italic max-w-2xl mx-auto">
          50 entreprises accompagnées en toute confidentialité — cadres, dirigeants,
          postes pivots. Les noms et profils complets restent à la discrétion des
          personnes qui nous ont recommandés.
        </p>
      </div>
    </section>
  );
}
